import {
  RequestableState,
  createRequestableState,
  handleDone,
  handleFailed,
  handleStarting,
} from '@tock/shared/state/requestableState';
import {
  Workflow,
  createWorkflowState,
  handleWorkflowDone,
} from '@tock/shared/workflow/workflowMonitor';
import { dashboard } from 'types';
import { payloadToJSON } from '@tock/coretypes';
import AppConstants from 'constants/AppConstants';

interface RequestableStateWorkflow extends RequestableState<dashboard.BusinessInventorySchedule[]> {
  workflow: Workflow;
}

type State = RequestableStateWorkflow;

const initialState: State = {
  ...createRequestableState<dashboard.BusinessInventorySchedule[]>(),
  workflow: createWorkflowState(),
};

export default function reducer(state = initialState, action: BaseTockAction): State {
  const { type } = action;

  switch (type) {
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_FETCH_ALL_STARTING:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_FETCH_STARTING:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_STARTING:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_ASYNC_STARTING:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_DUPLICATE_STARTING:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_CREATE_STARTING: {
      return handleStarting(state, action);
    }

    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_FETCH_ALL_DONE: {
      return handleDone(state, action);
    }

    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_FETCH_ALL_FAILED: {
      return handleFailed(state, action);
    }

    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_DUPLICATE_DONE:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_CREATE_DONE: {
      return {
        ...state,
        isRequesting: false,
        data: state.data
          ? [...state.data, payloadToJSON(action.payload)]
          : [payloadToJSON(action.payload)],
      };
    }

    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_FETCH_FAILED:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_CREATE_FAILED:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_FAILED:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_ASYNC_FAILED:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_ASYNC_WORKFLOW_RESULTS_FAILED:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_DUPLICATE_FAILED:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_DELETE_FAILED: {
      return {
        ...state,
        isRequesting: false,
      };
    }

    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_DELETE_DONE: {
      const { context } = action;

      if (!action.payload || !state.data) {
        return { ...state, isRequesting: false };
      }

      return {
        ...state,
        isRequesting: false,
        data: state.data.map((inventorySchedule) => {
          if (inventorySchedule.id === context.id) {
            return { ...inventorySchedule, state: 'ARCHIVED' };
          }
          return inventorySchedule;
        }),
      };
    }

    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_FETCH_DONE: {
      return addScheduleToState(
        state,
        payloadToJSON<dashboard.BusinessInventorySchedule>(action.payload)
      );
    }

    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_ASYNC_WORKFLOW_RESULTS_DONE:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_DONE: {
      return addScheduleToState(
        state,
        payloadToJSON<dashboard.BusinessInventoryScheduleUpdateResponse>(action.payload)
          .businessInventorySchedule as dashboard.BusinessInventorySchedule
      );
    }

    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_ASYNC_WORKFLOW_DONE:
    case AppConstants.BUSINESS_INVENTORY_SCHEDULES_UPDATE_ASYNC_DONE: {
      return {
        ...state,
        workflow: handleWorkflowDone(state.workflow, action.payload),
      };
    }

    default:
      return state;
  }
}

function addScheduleToState(
  state: State,
  scheduleFromResponse: dashboard.BusinessInventorySchedule
): State {
  return {
    ...state,
    isRequesting: false,
    data: !state.data
      ? [scheduleFromResponse]
      : state.data.map((schedule) => {
          return schedule.id === scheduleFromResponse.id ? scheduleFromResponse : schedule;
        }),
  };
}
